import type {
    DistributionResultData,
    DistributionStats,
    WeightDistributionData,
    WeightDistributionHistogram,
} from '@/domain/cages/weightDistribution.types';
import type { ApiError } from '@/domain/shared/errors';

const DEFAULT_DISTRIBUTION_TYPE = 'agg';

export type WeightDistributionState = {
    preferredIndex: number | undefined;
    distributionType: string;
    currentIndex: number;
    distributionStats: DistributionStats;
    day: string;
    dayToIndex: Record<string, number>;
    dayPartTag: string;
    weightDistributionData: WeightDistributionData[];
    weightDistributionHistogram: WeightDistributionHistogram[];
    weightUnitTag: string;
    isLoading: boolean;
    error: ApiError | null;
};

type WeightDistributionAction =
    | { type: 'SET_PREFERRED_INDEX'; payload: number | undefined }
    | { type: 'SET_DISTRIBUTION_TYPE'; payload: string }
    | { type: 'RESET_PREFERRED_INDEX' }
    | {
          type: 'SET_DATA';
          payload: { data: WeightDistributionData[]; dayPartTag: string; weightUnitTag: string };
      }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_ERROR'; payload: ApiError | null };

export const initialState: WeightDistributionState = {
    preferredIndex: undefined,
    distributionType: DEFAULT_DISTRIBUTION_TYPE,
    weightDistributionData: [],
    dayToIndex: {},
    currentIndex: 0,
    distributionStats: {
        cv: 0,
        dailyDistributionTypeTag: '',
        kurtosis: 0,
        skewness: 0,
        weight: 0,
        smallestFish: null,
        largestFish: null,
    } as DistributionStats,
    weightDistributionHistogram: [],
    isLoading: false,
    error: null,
    day: '',
    dayPartTag: 'day',
    weightUnitTag: 'g',
};

function calculateDerivedState(
    state: WeightDistributionState,
    newData?: { data: WeightDistributionData[]; dayPartTag: string; weightUnitTag: string }
): WeightDistributionState {
    const data = newData || {
        data: state.weightDistributionData,
        dayPartTag: state.dayPartTag,
        weightUnitTag: state.weightUnitTag,
    };

    // Filter and sort days
    const filteredDays =
        data.data
            ?.filter(
                (dd) => dd.dayPartTag === data.dayPartTag && dd.weightUnitTag === data.weightUnitTag
            )
            ?.sort((a, b) => (a.day > b.day ? 1 : a.day < b.day ? -1 : 0)) || [];

    // Create dayToIndex mapping
    const dayToIndex: Record<string, number> = filteredDays.reduce(
        (acc, cur, index) => {
            acc[cur.day] = index;
            return acc;
        },
        {} as Record<string, number>
    );

    // Calculate current index
    const currentIndex =
        filteredDays.length > 0
            ? state.preferredIndex === undefined ||
              state.preferredIndex === null ||
              state.preferredIndex >= filteredDays.length
                ? filteredDays.length - 1
                : state.preferredIndex
            : 0;

    const current = filteredDays[currentIndex];

    // Find datum for current distribution type
    const distributionStats = current?.data?.find(
        (datum: DistributionResultData) =>
            datum?.dailyDistributionTypeTag === state.distributionType
    );

    // Calculate smallest fish
    const smallestFish =
        current?.histograms && current.histograms.length > 0
            ? (current.histograms[0].bucket.from + current.histograms[0].bucket.to) / 2
            : null;

    // Filter histogram for current distribution type
    const histogram =
        current?.histograms?.filter(
            (histogram: WeightDistributionHistogram) =>
                histogram?.dailyDistributionTypeTag === state.distributionType
        ) || [];

    // Calculate largest fish
    const largestFish =
        histogram && histogram.length > 0
            ? (histogram[histogram.length - 1].bucket.from +
                  histogram[histogram.length - 1].bucket.to) /
              2
            : null;

    return {
        ...state,
        weightDistributionData: filteredDays,
        dayToIndex,
        currentIndex,
        distributionStats: distributionStats
            ? {
                  ...distributionStats,
                  smallestFish,
                  largestFish,
              }
            : ({
                  smallestFish,
                  largestFish,
              } as DistributionStats),
        weightDistributionHistogram: histogram,
        day: current?.day || '',
        dayPartTag: current?.dayPartTag || '',
        weightUnitTag: current?.weightUnitTag || '',
    };
}

export function weightDistributionReducer(
    state: WeightDistributionState,
    action: WeightDistributionAction
): WeightDistributionState {
    switch (action.type) {
        case 'SET_PREFERRED_INDEX':
            return calculateDerivedState({
                ...state,
                preferredIndex: action.payload,
            });
        case 'SET_DISTRIBUTION_TYPE':
            return calculateDerivedState({
                ...state,
                distributionType: action.payload,
            });
        case 'RESET_PREFERRED_INDEX':
            return calculateDerivedState({
                ...state,
                preferredIndex: undefined,
            });
        case 'SET_DATA':
            return calculateDerivedState({
                ...state,
                dayPartTag: action.payload.dayPartTag,
                weightDistributionData: action.payload.data,
                weightUnitTag: action.payload.weightUnitTag,
            });
        case 'SET_LOADING':
            return {
                ...state,
                isLoading: action.payload,
            };
        case 'SET_ERROR':
            return {
                ...state,
                error: action.payload,
            };
        default:
            return state;
    }
}
